import React, { useEffect, useState, useRef } from "react"
import Input from "@/components/core/Input/Input"
import Button from "@/components/core/Button/Button"
import ToolTip from "@/components/core/ToolTip/ToolTip"
import OrderWarning from "@/components/Shared/OrderStatus/v1/OrderWarning"
import {
  addFindMyOrderAnalytics,
  getEventInfo,
  getRecaptchaOrderStatusAnalytics,
  handleHelpTextAnalytics,
} from "@/components/Shared/OrderStatus/v1/analytics"
import {
  validateField,
  sanitizeTextForAnalytics,
  sanitizeInnerHtml,
} from "@/utils/helper"

import ReCaptcha, {
  validateRecaptcha,
} from "@/components/core/ReCaptcha/ReCaptcha"
import { useSelector } from "react-redux"
import { selectGenericState } from "@/store/features/genericSlice"
import OrderInReviewModal from "@/components/Shared/OrderInReviewModal"
import { isMobile } from "react-device-detect"
import { handleInReviewContinueButton } from "@/components/OrderConfirmation/OrderDetailsTabs/utils"

// import { log } from "@Services/logger.service"

const OrderStatus = ({ i18n, cartcontactDetails = [], ...props }) => {
  const { featureFlags = {} } = useSelector(selectGenericState)
  const { MYSQLCONNSTR_ENABLE_ORDER_DETAILS_TABS: isEnableTabView = false } =
    featureFlags
  const [showTooltip, setShowTooltip] = useState(false)
  const [data, setData] = useState({
    orderNo: { value: "", error: false, mssg: "" },
    email: { value: "", error: false, mssg: "" },
    error: { show: false, mssg: "" },
  })
  const [showAlert, setShowAlert] = useState(false)
  const statusAlert = useRef()
  const errorAlert = useRef()
  const {
    redirect,
    showToastMessage,
    inReviewModalInfo: {
      modalTitle = "",
      modalDescription = "",
      ctaPhoneNumber = "",
      ctaRedirectingURL = "",
      submittedModalTitle = "",
      submittedModalDescription = "",
    },
    isReceivedStatus,
    setIsReceived,
  } = props

  useEffect(() => {
    if (showTooltip) statusAlert.current.focus()
  }, [showTooltip])

  useEffect(() => {
    if (data.error.show) errorAlert.current.focus()
  }, [data.error.show])

  const handleSubmit = async () => {
    let valid = true
    const formData = {}
    const { email, orderNo } = data
    const errorMessage = []
    if (!orderNo.value) {
      formData.orderNo = { ...orderNo, error: true, mssg: i18n.emptyOrderNo }
      valid = false
      errorMessage.push(i18n.emptyOrderNo)
    }
    if (!email.value) {
      formData.email = { ...email, error: true, mssg: i18n.emptyEmail }
      valid = false
      errorMessage.push(i18n.emptyEmail)
    } else if (!(await validateField("email", email.value?.trim()))) {
      formData.email = { ...email, error: true, mssg: i18n.validEmail }
      valid = false
      errorMessage.push(i18n.validEmail)
    }
    setData({ ...data, ...formData })
    if (valid) {
      validateRecaptcha()
        .then(res => {
          if (res.success) {
            props
              .findOrder(orderNo.value, email.value)
              .then(check => {
                if (check === "showWarning") {
                  setShowAlert(true)
                  // message for in progress status
                  addFindMyOrderAnalytics({
                    action: "cmp:click",
                    status: "failure",
                    resp: data,
                    errorMessage: [
                      sanitizeTextForAnalytics(i18n.orderWarningDesc1),
                      sanitizeTextForAnalytics(i18n.orderWarningDesc2),
                    ],
                  })
                } else if (!check) {
                  setData({
                    ...data,
                    error: { show: true, mssg: i18n.cantFindOrder },
                  })
                  errorMessage.push(i18n.cantFindOrder)
                  addFindMyOrderAnalytics({
                    action: "cmp:click",
                    status: "failure",
                    resp: data,
                    errorMessage: errorMessage,
                  })
                } else {
                  addFindMyOrderAnalytics({
                    action: "cmp:click",
                    status: "success",
                    resp: data,
                    link:
                      check && typeof check === "string"
                        ? (check.indexOf("http") === -1
                            ? window.location.origin
                            : "") + check
                        : null,
                  })
                  // in case of success, we get url to redirect.
                  redirect(check)
                }
              })
              .catch(err => {
                addFindMyOrderAnalytics({
                  action: "cmp:click",
                  status: "failure",
                  resp: data,
                })
                // log.error(err)
                console.error(err)
              })
          } else {
            showToastMessage({ message: i18n.genError, isVisible: true })
          }
        })
        .catch(error => {
          // log.error(err)
          console.error(error)
        })
    } else {
      addFindMyOrderAnalytics({
        action: "cmp:click",
        status: "failure",
        resp: data,
        errorMessage: errorMessage,
      })
    }
  }

  const handleModalClose = () => {
    setIsReceived(false)
    setShowAlert(false)
  }

  const onBlurInput = async e => {
    const { name } = e.target
    const formData = {}
    const { email, orderNo } = data
    if (name === "orderNo") {
      if (!orderNo.value) {
        formData.orderNo = { ...orderNo, error: true, mssg: i18n.emptyOrderNo }
      }
    } else {
      if (!email.value) {
        formData.email = { ...email, error: true, mssg: i18n.emptyEmail }
      } else if (!(await validateField("email", email.value?.trim()))) {
        formData.email = { ...data.email, error: true, mssg: i18n.validEmail }
      }
    }
    setData({ ...data, ...formData })
  }

  const modalInfo = {
    modalTitle: isReceivedStatus ? submittedModalTitle : modalTitle,
    modalDescription: isReceivedStatus
      ? submittedModalDescription
      : modalDescription,
    buttonLabel: isReceivedStatus
      ? i18n?.orderSubmittedWarningCtaLabel || ""
      : i18n?.orderInReviewWarningContinueCta || "",
    buttonHandler: isReceivedStatus
      ? handleModalClose
      : () => {
          handleInReviewContinueButton(
            isMobile,
            ctaPhoneNumber,
            ctaRedirectingURL
          )
        },
  }

  return (
    <div className="order-status container-fluid">
      <div className="row no-gutters">
        <div className="col-lg-12 order-status__form-wrapper">
          <div className="order-status__banner">
            <div className="order-status__header">
              <div className="order-status__title">{i18n.orderStatus}</div>
              <div className="order-status__desc">
                {sanitizeInnerHtml(i18n.orderStatusDesc)}
              </div>
            </div>
          </div>
          <div className="order-status__form">
            <div className="order-status__form-content">
              {data.error.show && (
                <div
                  ref={errorAlert}
                  id="kf-order-status-error"
                  tabIndex="0"
                  role="alert"
                  className="order-status__error-box"
                >
                  <span>{sanitizeInnerHtml(data.error.mssg)}</span>
                </div>
              )}
              <Input
                id="kf-order-status-number"
                type="text"
                name="orderNo"
                value={data.orderNo.value}
                placeholder={`${i18n.orderNo}*`}
                label={`${i18n.orderNo}*`}
                onChange={e =>
                  setData({
                    ...data,
                    orderNo: { value: e.target.value, error: false, mssg: "" },
                    error: { show: false, mssg: "" },
                  })
                }
                showError={data.orderNo.error}
                errorMessage={data.orderNo.mssg}
                onBlur={onBlurInput}
              />
              <div
                className="order-status__help-text"
                role="link"
                tabIndex="0"
                onClick={() => {
                  setShowTooltip(!showTooltip)
                  handleHelpTextAnalytics()
                }}
              >
                {i18n.wheresOrderNo}
              </div>
              <div
                className={`order-status__tooltip${
                  showTooltip ? "--show" : "--hide"
                }`}
              >
                <div
                  ref={statusAlert}
                  role="tooltip"
                  tabIndex="0"
                  aria-label={i18n.tooltipMssg}
                  id="kf-order-status-tooltip"
                  className="order-status__tooltip-wrapper"
                  onBlur={() => setShowTooltip(false)}
                >
                  <ToolTip message={i18n.tooltipMssg} />
                </div>
              </div>
              <Input
                id="kf-order-status-email"
                type="text"
                name="email"
                maxLength={60}
                value={data.email.value}
                placeholder={`${i18n.email}*`}
                label={`${i18n.email}*`}
                onChange={e =>
                  setData({
                    ...data,
                    email: { value: e.target.value, error: false, mssg: "" },
                    error: { show: false, mssg: "" },
                  })
                }
                showError={data.email.error}
                errorMessage={data.email.mssg}
                onBlur={onBlurInput}
              />
              <Button
                customClass="order-status__find-btn"
                type="black"
                flexible={true}
                label={i18n.findOrder}
                onClick={handleSubmit}
                tag={"button"}
              />
              <ReCaptcha getAnalyticsData={getRecaptchaOrderStatusAnalytics} />
              <div className="order-status__support">
                <span>{i18n.needHelpOrder}</span>
                {cartcontactDetails
                  ? cartcontactDetails?.map(obj => {
                      const { contactnumbertitle = "", contactnumber = "" } =
                        obj ?? {}
                      return (
                        <p
                          key={contactnumbertitle}
                          className="cart-info__brand-title"
                        >
                          {contactnumbertitle}
                          {`:   `}
                          <a
                            aria-label={`${i18n.callSupport} ${contactnumber}`}
                            key={contactnumber}
                            className="cart-info__link gbh-data-layer"
                            href={`tel:${contactnumber}`}
                            data-gbh-data-layer={getEventInfo(
                              "order status",
                              "call support phone number",
                              contactnumber
                            )}
                          >
                            {contactnumber}
                          </a>
                        </p>
                      )
                    })
                  : null}
              </div>
            </div>
          </div>
        </div>
        {showAlert ? (
          isEnableTabView ? (
            <OrderInReviewModal
              showAlert={showAlert}
              modalInfo={modalInfo}
              handleClose={handleModalClose}
            />
          ) : (
            <OrderWarning
              showAlert={showAlert}
              closeAlert={setShowAlert}
              i18n={i18n}
            />
          )
        ) : null}
      </div>
    </div>
  )
}

export default OrderStatus
